import { reactMount } from '@/lib/reactMount'
import { Chevron, ArrowRight } from '@/components/Icons'
import { Fragment, useEffect, useRef, useState, useMemo } from 'react'
import { Button } from '@/components/Button'
import { FormField } from '@/components/FormField'
import { LoadingSpinner } from '@/components/LoadingSpinner'
import { ErrorMessage } from '@/components/ErrorMessage'
import useSWRMutation from 'swr/mutation'
import { fetcher } from '@/lib/fetcher'

const DealerCard = ({ dealer }) => {
  const logoSrc = dealer?.Logo?.Url && String(dealer.Logo.Url).trim() !== ''
    ? window.location.origin + dealer.Logo.Url
    : '/find-a-dealer-default.png'

  const websiteUrl = dealer?.Website && String(dealer.Website).trim() !== ''
    ? dealer.Website
    : null

  const optionalCtaUrl = (() => {
    if (!dealer?.OptionalCTA) return null
    if (typeof dealer.OptionalCTA === 'string') return dealer.OptionalCTA
    if (typeof dealer.OptionalCTA === 'object' && dealer.OptionalCTA.Url) return dealer.OptionalCTA.Url
    return null
  })()

  const optionalCtaText = (() => {
    if (!dealer?.OptionalCTA) return 'Apply for finance'
    if (typeof dealer.OptionalCTA === 'object' && dealer.OptionalCTA.Name) return dealer.OptionalCTA.Name
    return 'Apply for finance'
  })()

  const optionalCtaTarget = (() => {
    if (!dealer?.OptionalCTA) return null
    if (typeof dealer.OptionalCTA === 'object' && dealer.OptionalCTA.Target) return dealer.OptionalCTA.Target
    return null
  })()

  return (
    <article className='space-y-4 flex flex-col' data-theme='light'>
      <figure className='flex aspect-[250/42] max-w-[250px] relative'>
        <img
          src={logoSrc}
          alt={`${dealer?.Name || 'Dealer'} logo`}
          className='w-full h-full absolute inset-0 max-w-full max-h-full object-contain object-[left_center]'
        />
      </figure>

      <header className='space-y-1'>
        <h3 className='text-default-900 font-extrabold text-xl'>
          {dealer?.Name || 'Dealer'}
        </h3>
        <div data-spacer />
        {(dealer?.Address || dealer?.Region) && (
          <p className='text-base font-semibold text-default-600'>
            {/* Split address by line breaks to render multi-line addresses properly (e.g., "PO Box 123\nCity\nCountry") */}
            {(dealer?.Address || dealer?.Region).split('\n').map((line, index, array) => (
              <Fragment key={index}>
                {line}
                {index < array.length - 1 && <br />}
              </Fragment>
            ))}
          </p>
        )}
      </header>

      <div className='flex flex-col gap-3 grow justify-end'>
        <div>
          <div className='w-full flex gap-3 grow items-center'>
            {websiteUrl && (
              <Button href={websiteUrl} target='_blank' variant='secondary' size='sm' className='!px-2 grow'>
                <span className='flex items-center gap-2'>
                  View website <ArrowRight className='size-4' />
                </span>
              </Button>
            )}
            {optionalCtaUrl && (
              <Button href={optionalCtaUrl} target={optionalCtaTarget || undefined} variant='link' size='sm' className='!px-2'>
                <span className='flex items-center gap-2'>
                  {optionalCtaText} <ArrowRight className='size-4' />
                </span>
              </Button>
            )}
          </div>
        </div>
      </div>
    </article>
  )
}

// Filter Section
const Filter = ({
  regionOptions,
  productOptions,
  brandOptions,
  conditionOptions,
  handleProductChange,
  selectedProduct,
  handleBrandChange,
  selectedBrand,
  handleConditionChange,
  selectedCondition,
  handleRegionChange,
  selectedRegion
}) => {
  const shouldShowCondition = productOptions.find(opt => opt.Name === selectedProduct)?.Value === 'MV'

  return (
    <form className='mx-auto max-w-[1060px] px-4 xl:px-3 pb-14'>
      <div className={`grid grid-cols-1 md:grid-cols-2 ${shouldShowCondition ? 'xl:grid-cols-4' : 'xl:grid-cols-3'} gap-y-6 md:gap-3`}>
        <FormField
          type='select'
          label='Products'
          id='product'
          name='product'
          value={selectedProduct}
          options={productOptions.map(option => option.Name)}
          onChange={handleProductChange}
          placeholder='Select'
        />
        <FormField
          type='select'
          label='Brand'
          id='brand'
          name='brand'
          value={selectedBrand}
          options={brandOptions.map(option => option.Name)}
          placeholder='Select'
          onChange={handleBrandChange}
        />
        {shouldShowCondition && (
          <FormField
            type='select'
            label='Condition'
            id='condition'
            name='condition'
            value={selectedCondition}
            options={conditionOptions}
            onChange={handleConditionChange}
            placeholder='Select'
          />
        )}
        <FormField
          type='select'
          label='Region'
          id='region'
          name='region'
          value={selectedRegion}
          options={regionOptions}
          onChange={handleRegionChange}
          placeholder='Select'
        />
      </div>
    </form>
  )
}

export const FindDealerPage = ({ dataset, umbraco }) => {
  const umbracoRef = useRef(umbraco || JSON.parse(dataset.umbraco))
  umbraco ||= umbracoRef.current

  const [selectedProduct, setSelectedProduct] = useState('')
  const [selectedBrand, setSelectedBrand] = useState('')
  const [selectedCondition, setSelectedCondition] = useState('')
  const [selectedRegion, setSelectedRegion] = useState('')
  const hasInitialisedSelectedProductFromUrl = useRef(false)

  const {
    trigger: triggerSearch,
    data: dealerBuyingOptionsResults,
    error,
    isMutating: isLoading
  } = useSWRMutation('/api/data/DealerBuyingOptions', fetcher)

  useEffect(() => {
    triggerSearch({ method: 'GET' })
  }, [triggerSearch])

  const productOptions = dealerBuyingOptionsResults?.data || []
  const brandOptions = umbraco.Brands || []
  const conditionOptions = ['New', 'Used']

  // Initialize selectedProduct from URL query param `product` when options are available
  useEffect(() => {
    if (hasInitialisedSelectedProductFromUrl.current) return
    const params = new URLSearchParams(window.location.search)
    const productParam = params.get('product')
    if (!productParam || !Array.isArray(productOptions) || productOptions.length === 0) return

    const matched = productOptions.find(opt => String(opt.Value) === String(productParam) || String(opt.Name) === String(productParam))
    if (matched) {
      setSelectedProduct(matched.Name)
      hasInitialisedSelectedProductFromUrl.current = true
    }
  }, [productOptions])

  const regionOptions = useMemo(() => {
    const regions = (umbraco.Dealers || [])
      .map(dealer => dealer.Region)
      .filter(region => region && region.trim() !== '')

    // Deduplicate case-insensitively while preserving first-seen casing
    const seen = new Set()
    const uniqueRegions = []
    for (const region of regions) {
      const key = String(region).toLocaleLowerCase()
      if (!seen.has(key)) {
        seen.add(key)
        uniqueRegions.push(region)
      }
    }

    // Case-insensitive sort so "east coast" appears with "E"
    return uniqueRegions.sort((a, b) =>
      String(a).localeCompare(String(b), undefined, { sensitivity: 'base' })
    )
  }, [umbraco.Dealers])

  const handleProductChange = (e) => {
    setSelectedProduct(e.target.value)
  }

  const handleBrandChange = (e) => {
    setSelectedBrand(e.target.value)
  }

  const handleConditionChange = (e) => {
    setSelectedCondition(e.target.value)
  }

  const handleRegionChange = (e) => {
    setSelectedRegion(e.target.value)
  }

  const dealers = umbraco.Dealers || []
  const [currentPage, setCurrentPage] = useState(1)
  const [itemsPerPage, setItemsPerPage] = useState(12)

  const handlePageChange = (page) => {
    if (page >= 1 && page <= totalPages) {
      setCurrentPage(page)
    }
  }

  const handleItemsPerPageChange = (e) => {
    const newItemsPerPage = parseInt(e.target.value)
    setItemsPerPage(newItemsPerPage)
    setCurrentPage(1)
  }

  // Reset to first page when filters change
  useEffect(() => {
    setCurrentPage(1)
  }, [selectedProduct, selectedBrand, selectedCondition, selectedRegion])

  const getPageNumbers = () => {
    const pages = []
    const maxVisiblePages = 5

    if (totalPages <= maxVisiblePages) {
      for (let i = 1; i <= totalPages; i++) {
        pages.push(i)
      }
    } else {
      if (currentPage <= 3) {
        for (let i = 1; i <= 4; i++) {
          pages.push(i)
        }
        pages.push('...')
        pages.push(totalPages)
      } else if (currentPage >= totalPages - 2) {
        pages.push(1)
        pages.push('...')
        for (let i = totalPages - 3; i <= totalPages; i++) {
          pages.push(i)
        }
      } else {
        pages.push(1)
        pages.push('...')
        for (let i = currentPage - 1; i <= currentPage + 1; i++) {
          pages.push(i)
        }
        pages.push('...')
        pages.push(totalPages)
      }
    }

    return pages
  }

  const filteredDealers = useMemo(() => {
    const selectedProductOption = Array.isArray(productOptions)
      ? productOptions.find(opt => opt.Name === selectedProduct)
      : null

    const selectedBrandOption = Array.isArray(brandOptions)
      ? brandOptions.find(opt => opt.Name === selectedBrand)
      : null

    const productCode = selectedProductOption?.Value || null
    const brandCode = selectedBrandOption?.Value || null

    const filteredDealers = dealers.filter(dealer => {
      // Product filter => dealer.BuyingOptions contains productCode
      if (productCode) {
        const dealerOptions = Array.isArray(dealer?.BuyingOptions) ? dealer.BuyingOptions : []
        if (!dealerOptions.includes(String(productCode))) return false
      }

      // Brand filter => dealer.Brand equals brandCode or selectedBrand
      if (selectedBrand) {
        const dealerBrand = dealer?.Brand
        const matchBrand = dealerBrand === selectedBrand || (brandCode ? dealerBrand === brandCode : false)
        if (!matchBrand) return false
      }

      // Condition filter (applies typically for MV)
      if (selectedCondition) {
        const conditions = Array.isArray(dealer?.CarConditions) ? dealer.CarConditions : []
        const normalizedSelectedCondition = selectedCondition.toLowerCase()
        if (!conditions.includes(normalizedSelectedCondition)) return false
      }

      // Region filter
      if (selectedRegion) {
        if (dealer?.Region !== selectedRegion) return false
      }

      return true
    })
      .sort((a, b) => String(a?.Name || '').localeCompare(String(b?.Name || '')))

    console.log('Filtered Dealers', filteredDealers)
    return filteredDealers
  }, [dealers, productOptions, brandOptions, selectedProduct, selectedBrand, selectedCondition, selectedRegion])

  const totalItems = filteredDealers.length
  const totalPages = Math.max(1, Math.ceil(totalItems / itemsPerPage))
  const startIndex = (currentPage - 1) * itemsPerPage
  const endIndex = Math.min(startIndex + itemsPerPage, totalItems)
  const currentDealers = filteredDealers.slice(startIndex, endIndex)

  return (
    <>
      <section className='relative isolate py-24 md:px-4 lg:px-10 xl:px-24'>
        <header className='max-w-3xl text-center mx-auto mb-12'>
          {umbraco.Title && (
            <h1 className='text-4xl font-extrabold tracking-tight text-gray-900 sm:text-5xl'>
              {umbraco.Title}
            </h1>
          )}
          {umbraco.Summary && (
            <div className='_simple-rte mt-4 text-base text-balance font-semibold' dangerouslySetInnerHTML={{ __html: umbraco.Summary }} />
          )}
        </header>

        <Filter
          productOptions={productOptions}
          brandOptions={brandOptions}
          conditionOptions={conditionOptions}
          regionOptions={regionOptions}
          handleProductChange={handleProductChange}
          selectedProduct={selectedProduct}
          handleBrandChange={handleBrandChange}
          selectedBrand={selectedBrand}
          handleConditionChange={handleConditionChange}
          selectedCondition={selectedCondition}
          handleRegionChange={handleRegionChange}
          selectedRegion={selectedRegion}
        />

        <div className='mx-auto max-w-[1060px] px-4 xl:px-3 space-y-16'>
          <div className='mx-auto space-y-8'>
            {isLoading && <LoadingSpinner message='Loading dealers...' />}

            {!isLoading && currentDealers.length > 0 && (
              <div className='grid grid-cols-1 gap-x-8 gap-y-10 md:gap-y-16 sm:grid-cols-2 lg:grid-cols-3'>
                {currentDealers.map((dealer, idx) => (
                  <DealerCard key={`${dealer.Name}-${idx}`} dealer={dealer} />
                ))}
              </div>
            )}

            {!isLoading && !error && filteredDealers.length === 0 && dealers.length > 0 && (
              <div className='text-center '>
                <p className='text-gray-500 text-lg'>
                  No Dealers found, please update filters and try again.
                </p>
              </div>
            )}

            {!isLoading && error && (
              <ErrorMessage message='Failed to load dealers. Please refresh the page.' />
            )}
          </div>
          {/* Pagination */}
          {totalPages > 1 && (
            <nav
              aria-label='Pagination'
              className='w-full'
            >
              <div className='hidden lg:flex flex-row items-center justify-between w-full'>
                <p className='text-default-500 font-semibold text-sm max-lg:grow text-center shrink-0'>
                  Page {currentPage} of{' '}
                  {totalPages}
                </p>

                <div className='flex flex-row gap-1 grow justify-end'>

                  {/* Previous button */}
                  <button
                    onClick={() => handlePageChange(currentPage - 1)}
                    disabled={currentPage === 1}
                    className={`flex items-center gap-1 pl-4 pr-5 pt-[7px] pb-[9px] rounded border font-extrabold text-sm shadow-sm transition duration-300 ${currentPage === 1
                                    ? 'text-default-300 border-default-300 pointer-events-none'
                                    : 'border-default-900 text-default-900'
                                  }`}
                  >
                    <ArrowRight className='rotate-180 size-6' />
                    <span>Previous</span>
                  </button>

                  {/* Page numbers */}
                  {getPageNumbers().map((page, index) => (
                    <Fragment key={index}>
                      {page === '...' && (
                        <span className='flex justify-center items-center pb-2.5 bg-default-50 border border-default-200 text-default-900 text-center font-extrabold text-sm rounded px-1 py-2 w-11 shadow-sm'>
                          ...
                        </span>
                      )}
                      {page !== '...' && (
                        <button
                          onClick={() => handlePageChange(page)}
                          className={`flex justify-center items-center pb-2.5 font-extrabold text-sm rounded px-1 py-2 w-11 shadow-sm hover:bg-default-900 hover:text-default-50 transition duration-300 ${page === currentPage
                                          ? 'z-10 text-default-50 bg-primary-950 border border-primary-950 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary-950'
                                          : 'bg-default-50 border border-default-200 text-default-900'
                                        }`}
                        >
                          {page}
                        </button>
                      )}
                    </Fragment>
                  ))}

                  {/* Next button */}
                  <button
                    onClick={() => handlePageChange(currentPage + 1)}
                    disabled={currentPage === totalPages}
                    className={`flex justify-center items-center gap-1 pl-5 max-lg:pr-5 pr-4 pt-[7px] pb-2 lg:pb-[9px] rounded border border-default-900 text-default-900 font-extrabold text-sm shadow-sm hover:bg-default-900 hover:text-default-50 transition duration-300 ${currentPage === totalPages
                                    ? 'text-default-300 border-default-300 pointer-events-none'
                                    : 'border-default-900 text-default-900'
                                  }`}
                  >
                    <span>Next</span>
                    <ArrowRight className='size-6' />
                  </button>

                </div>
                {/* Items per page selector */}
                {totalItems >= 12 && (
                  <label className='relative ml-12'>
                    <select
                      id='itemsPerPage'
                      name='itemsPerPage'
                      value={itemsPerPage}
                      onChange={handleItemsPerPageChange}
                      className='cursor-pointer bg-default-50 h-11 w-full rounded-lg border border-default-200 focus:border-default-400 pb-px pr-10 pl-3 font-semibold text-[14px] text-default-700 appearance-none shadow-sm'
                    >
                      <option value={12}>12 / page</option>
                      <option value={24}>24 / page</option>
                      <option value={48}>48 / page</option>
                    </select>
                    <Chevron className='text-default-700 pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 size-5' />
                  </label>
                )}
              </div>

              {/* Mobile pagination */}
              <div className='flex flex-row items-center justify-between w-full lg:hidden'>
                <button
                  onClick={() => handlePageChange(currentPage - 1)}
                  disabled={currentPage === 1}
                  className={`flex justify-center items-center gap-1 pl-5 max-lg:pr-5 pr-4 pt-[7px] pb-2 lg:pb-[9px] rounded border font-extrabold text-sm shadow-sm hover:bg-default-900 hover:text-default-50 transition duration-300 ${currentPage === 1
                                ? 'text-default-300 border-default-300 pointer-events-none'
                                : 'border-default-900 text-default-900'
                              }`}
                >
                  <span className='sr-only'>previous</span>
                  <ArrowRight className='rotate-180 size-6' />
                </button>
                <p className='text-default-500 font-semibold text-sm grow text-center'>
                  Page {currentPage} of{' '}
                  {totalPages}
                </p>
                <button
                  onClick={() => handlePageChange(currentPage + 1)}
                  disabled={currentPage === totalPages}
                  className={`flex justify-center items-center gap-1 pl-5 max-lg:pr-5 pr-4 pt-[7px] pb-2 lg:pb-[9px] rounded border border-default-900 text-default-900 font-extrabold text-sm shadow-sm hover:bg-default-900 hover:text-default-50 transition duration-300 ${currentPage === totalPages
                                ? 'text-default-300 border-default-300 pointer-events-none'
                                : 'border-default-900 text-default-900'
                              }`}
                >
                  <span className='sr-only'>Next</span>
                  <ArrowRight className='size-6' />
                </button>
              </div>
            </nav>
          )}
        </div>
      </section>
    </>
  )
}

reactMount('FindDealerPage', FindDealerPage)
